import React, { useCallback, useEffect, useRef, useState } from 'react';

// Must stay in sync with the header height in Header.tsx
const HEADER_HEIGHT = 80;
// Max the user can drag to — leaves a thin clickable overlay strip at the bottom
const MAX_PANEL_RATIO = 0.92;
const HANDLE_HEIGHT = 40;

interface SlideOutPanelProps {
  isOpen: boolean;
  onClose: () => void;
  children?: React.ReactNode;
  leftOffset?: number;
}

/**
 * SlideOutPanel slides DOWN from the bottom edge of the header.
 *
 * height:auto the panel always fits its content regardless of zoom level,
 * so content is never clipped or scrollable.
 *
 * dragMinHeight dragging the bottom handle sets a min-height, forcing
 * the panel to be at least that tall (expanding beyond natural content size).
 * Dragging back up lowers min-height; once it falls below the natural content
 * height the panel snaps back to its natural size automatically.
 */
export const SlideOutPanel: React.FC<SlideOutPanelProps> = ({ isOpen, onClose, children, leftOffset = 0 }) => {
  // 0 means no override; the panel sits at its natural content height
  const [dragMinHeight, setDragMinHeight] = useState(0);
  const isDraggingRef = useRef(false);
  // The panel's original content-only height (without any drag expansion).
  // This is the floor: dragging above this resets the panel to auto height.
  // It is only captured when the panel is NOT expanded (dragMinHeight === 0).
  const contentHeightRef = useRef(0);
  const panelRef = useRef<HTMLDivElement>(null);

  // Reset when panel closes
  useEffect(() => {
    if (!isOpen) {
      setDragMinHeight(0);
      contentHeightRef.current = 0;
    }
  }, [isOpen]);

  // Re-clamp dragMinHeight if the viewport shrinks
  useEffect(() => {
    const handle = () => {
      const max = (window.innerHeight - HEADER_HEIGHT) * MAX_PANEL_RATIO;
      setDragMinHeight(h => Math.min(h, max));
    };
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);

  // ── Pointer handlers ───────────────────────────────────────────────────────
  // setPointerCapture routes all pointermove/pointerup to this element even
  // after the cursor moves outside it, so the drag is always tracked.

  const onHandlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    isDraggingRef.current = true;
    const renderedHeight = panelRef.current?.getBoundingClientRect().height ?? 0;
    // Only capture the content height when the panel is at its natural size.
    // When already expanded, keep the previously stored content height as the floor.
    if (dragMinHeight === 0) {
      contentHeightRef.current = renderedHeight;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [dragMinHeight]);

  const onHandlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return;
    e.preventDefault();
    const max = (window.innerHeight - HEADER_HEIGHT) * MAX_PANEL_RATIO;
    const desired = e.clientY - HEADER_HEIGHT;
    // Collapse to auto only if dragged above the original content height
    if (desired <= contentHeightRef.current) {
      setDragMinHeight(0);
    } else {
      setDragMinHeight(Math.min(desired, max));
    }
  }, []);

  const onHandlePointerUp = useCallback(() => {
    isDraggingRef.current = false;
  }, []);

  return (
    <>
      {/* Dim overlay — covers area below header to the right of menu, click to close */}
      <div
        className={`fixed right-0 bottom-0 z-[104] transition-opacity duration-300 bg-black/40 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ top: HEADER_HEIGHT, left: leftOffset }}
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className={`fixed right-0 z-[105] flex flex-col ${
          isOpen ? 'translate-y-0' : '-translate-y-full'
        }`}
        style={{
          top: HEADER_HEIGHT,
          left: leftOffset,
          // height:auto → panel wraps content naturally (no clipping at any zoom level)
          // Once the user drags we switch to an explicit px height so that flex-1
          // on the content div has a concrete parent height to fill into.
          height: dragMinHeight > 0 ? dragMinHeight : 'auto',
          maxHeight: `${MAX_PANEL_RATIO * 100}%`,
          background: 'var(--color-panel-bg, #18181b)',
          boxShadow: '0 8px 40px rgba(0,0,0,0.45)',
          transition: 'transform 300ms ease-in-out',
          overflow: 'hidden',
        }}
        role="dialog"
        aria-modal="true"
      >
        <button
          className="absolute top-4 right-4 z-20 text-gray-400 hover:text-gray-200 focus:outline-none text-2xl leading-none"
          onClick={onClose}
          aria-label="Close panel"
        >
          x
        </button>

        {/* Content — flex-1 absorbs extra space when min-height > natural content height,
             so the handle always sits at the bottom edge and content fills the panel */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {children}
        </div>

        {/* Drag handle */}
        <div
          className="flex w-full flex-shrink-0 cursor-row-resize items-center justify-center select-none"
          style={{ height: HANDLE_HEIGHT, touchAction: 'none', background: 'transparent' }}
          onPointerDown={onHandlePointerDown}
          onPointerMove={onHandlePointerMove}
          onPointerUp={onHandlePointerUp}
          onPointerCancel={onHandlePointerUp}
          aria-hidden="true"
        >
          <div className="rounded-full" style={{ width: 48, height: 4, background: 'rgba(255,255,255,0.35)' }} />
        </div>
      </div>
    </>
  );
};
